// We deliberately do not export setTimeout-based "scheduled" notifications.
// They cannot fire when the app is closed, which is when reminders matter most.
// Honest options for true scheduled reminders are tracked in TODO.md item #1
// (Web Push + backend, or Capacitor wrapping). What we do here is:
//   - request OS notification permission once on startup,
//   - expose a fire-and-forget showNotification() for in-session toasts,
//   - expose computeMissedReminders() so the UI can flag missed times-of-day
//     when the user opens the app (the only moment we can guarantee).

import type { CheckIn, ReminderTimes, TenthStep } from './db';
import { getTenthStepCutoff, parseHm } from './timeUtils';

export type MissedKind = 'morning' | 'noon' | 'evening' | 'tenth-step';

export interface MissedReminder {
  kind: MissedKind;
  scheduledFor: string; // HH:MM
  label: string;
}

export function isNotificationSupported(): boolean {
  return 'Notification' in window && 'serviceWorker' in navigator;
}

export type NotificationPermissionState =
  | 'unsupported'
  | 'default'
  | 'granted'
  | 'denied';

export function getNotificationPermissionState(): NotificationPermissionState {
  if (!isNotificationSupported()) return 'unsupported';
  return Notification.permission;
}

export async function requestNotificationPermission(): Promise<boolean> {
  if (!isNotificationSupported()) {
    return false;
  }
  try {
    const permission = await Notification.requestPermission();
    return permission === 'granted';
  } catch (error) {
    console.error('Error requesting notification permission:', error);
    return false;
  }
}

export function hasNotificationPermission(): boolean {
  return isNotificationSupported() && Notification.permission === 'granted';
}

export interface NotificationPayload {
  body: string;
  icon?: string;
  data?: Record<string, unknown>;
}

export async function showNotification(
  title: string,
  options: NotificationPayload,
): Promise<void> {
  if (!hasNotificationPermission()) return;
  const registration = await navigator.serviceWorker.ready;
  await registration.showNotification(title, {
    icon: '/logo192.png',
    ...options,
  });
}

const CHECKIN_LABELS: Record<'morning' | 'noon' | 'evening', string> = {
  morning: 'Morning check-in',
  noon: 'Noon check-in',
  evening: 'Evening check-in',
};

function timeIsPastNow(time: string, now: Date): boolean {
  const parsed = parseHm(time);
  if (!parsed) return false;
  if (parsed.hour < now.getHours()) return true;
  if (parsed.hour > now.getHours()) return false;
  return parsed.minute <= now.getMinutes();
}

interface MissedReminderInputs {
  reminderTimes: ReminderTimes;
  notificationsEnabled: boolean;
  todayCheckIns: CheckIn[];
  todayTenthStep: TenthStep | null;
  /** Override "now" for tests. */
  now?: Date;
}

export function computeMissedReminders({
  reminderTimes,
  notificationsEnabled,
  todayCheckIns,
  todayTenthStep,
  now = new Date(),
}: MissedReminderInputs): MissedReminder[] {
  if (!notificationsEnabled) return [];

  const missed: MissedReminder[] = [];

  for (const kind of ['morning', 'noon', 'evening'] as const) {
    const scheduledFor = reminderTimes[kind];
    if (!scheduledFor) continue;
    if (!timeIsPastNow(scheduledFor, now)) continue;
    if (todayCheckIns.some((ci) => ci.type === kind)) continue;
    missed.push({
      kind,
      scheduledFor,
      label: CHECKIN_LABELS[kind],
    });
  }

  // Treat the latest evening reminder as the implicit deadline for the
  // 10th-step nightly review. If no evening time is set (or it's malformed),
  // fall back to 21:00. `getTenthStepCutoff` handles the empty-string case
  // that `??` alone would miss.
  const tenthStepCutoff = getTenthStepCutoff(reminderTimes);
  if (timeIsPastNow(tenthStepCutoff, now) && !todayTenthStep?.isCompleted) {
    missed.push({
      kind: 'tenth-step',
      scheduledFor: tenthStepCutoff,
      label: '10th step inventory',
    });
  }

  return missed;
}
